import React, { FC, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { TydeSideMenu } from 'broentech';
import cn from 'classnames';

import styles from './App.module.scss';
import { menuItems } from 'menu/MenuItems';
import { TydeHeader } from 'components/Header/TydeHeader';
import { LandingPage } from 'pages/LandingPage';
import PrivateRoute from './private-route';
import { useAppDispatch, useAppSelector } from 'store/storeHooks';
import { sideMenuActions } from 'store/side-menu/side-menu-slice';
import PublicRoute from './public-route';
import { useAuthStatus } from 'auth/useAuthStatus';
import { userManager } from 'auth/oidc';
import { useAuthActions } from 'auth/useAuthActions';

const App: FC<any> = () => {

  const dispatch = useAppDispatch();
  const sideMenuState = useAppSelector(state => state.sideMenu);

  const { isAuthenticated, isLoading } = useAuthStatus();
  const { initiateSilentSignIn } = useAuthActions();

  useEffect(() => {
    const initializeAuth = async (): Promise<void> => {
      try {
        const user = await userManager.getUser();
        if (user && user.expired) {
          await initiateSilentSignIn();
        }
      } catch (error) {
        console.error('Initialization error', error);
        // Currently, errors are handled in the useAuthActions hook. Consider throwing from the hook to handle where it's used instead.
      }
    };

    // TODO: Remove after a few months
    // Remove deprecated authState from localStorage
    if (localStorage.getItem('authState')) {
      localStorage.removeItem('authState');
    }

    initializeAuth();
  }, []);

  const sideMenuToggleHandler = (isExpanded: boolean) => {
    if (isExpanded) {
      dispatch(sideMenuActions.expand());
    } else {
      dispatch(sideMenuActions.collapse());
    }
  };

  const sideMenu = isAuthenticated ? <TydeSideMenu menuItems={menuItems} onToggle={sideMenuToggleHandler} /> : undefined;

  useEffect(() => {
    if (isAuthenticated) {
      window.history.replaceState({}, '', '/');
    }
  }, [isAuthenticated]);

  return (
    <div className={styles.App}>

      { sideMenu }

      <TydeHeader />

      <div className={cn(styles.appContent, {
        [styles.withSideMenuExpanded]: isAuthenticated && sideMenuState.isExpanded,
        [styles.withSideMenu]: isAuthenticated && !sideMenuState.isExpanded,
      })}>
        <Routes>
          <Route element={
            <PublicRoute
              restricted
              isAuthenticated={isAuthenticated}
              isLoading={isLoading}
            />
          }>
            <Route path="/" element={<LandingPage />} />
          </Route>

          <Route path="/logout" element={<Navigate to="/" />} />

          <Route element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              isLoading={isLoading}
            />
          }>
            {
              menuItems.map(
                (menuItem, index) => (
                  <Route
                    key={index}
                    path={menuItem.route}
                    element={menuItem.page}
                  />
                )
              )
            }
          </Route>
        </Routes>
      </div>
    </div>
  );
};

export default App;
